/**
 * Wallet Asset Detail Page
 * Shows YOUR holding of a specific coin/token — price, position, actions, and activity.
 * Distinct from CoinDetailPage (which is the general Market browsing page).
 *
 * @package ChainVault
 * @since   1.0.0
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import { fetchTransactions, fetchTokenTransfers } from '../utils/etherscan';
import { fetchSolTransactions } from '../utils/solana';
import { sendAsset } from '../utils/sendAsset';
import toast from 'react-hot-toast';
import { playSendSound, playErrorSound, resumeAudio } from '../utils/sounds';

const COINGECKO_URL = 'https://api.coingecko.com/api/v3';
const PROXY_URL = ( window.chainvaultSettings && window.chainvaultSettings.siteUrl )
    ? window.chainvaultSettings.siteUrl.replace( /\/+$/, '' ) + '/market'
    : COINGECKO_URL;

function getCoinGeckoHeaders() {
    const key = ( window.chainvaultSettings && window.chainvaultSettings.coingeckoApiKey ) || '';
    return key ? { 'x-cg-demo-api-key': key } : {};
}

const NATIVE_SYMBOL_BY_NETWORK = { ethereum: 'ETH', bsc: 'BNB', polygon: 'MATIC', base: 'ETH', solana: 'SOL' };

function formatPrice( p ) {
    if ( p == null ) return '—';
    if ( p >= 1000 ) return '$' + p.toLocaleString( 'en-US', { maximumFractionDigits: 2 } );
    if ( p >= 1    ) return '$' + p.toFixed( 2 );
    if ( p >= 0.01 ) return '$' + p.toFixed( 4 );
    return '$' + p.toFixed( 8 );
}

function timeAgo( ts ) {
    if ( ! ts ) return '';
    const seconds = Math.floor( ( Date.now() / 1000 ) - ts );
    if ( seconds < 60 ) return seconds + 's ago';
    const minutes = Math.floor( seconds / 60 );
    if ( minutes < 60 ) return minutes + 'm ago';
    const hours = Math.floor( minutes / 60 );
    if ( hours < 24 ) return hours + 'h ago';
    const days = Math.floor( hours / 24 );
    return days + 'd ago';
}

function InfoRow( { label, value, last } ) {
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: last ? 'none' : '1px solid rgba(255,255,255,0.06)' }}>
            <span style={{ color: '#888', fontSize: '14px' }}>{ label }</span>
            <span style={{ color: '#fff', fontSize: '14px', fontWeight: '600' }}>{ value }</span>
        </div>
    );
}

const actionBtnStyle = {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '14px',
    color: '#fff',
    padding: '14px 0',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
};

const statCardStyle = {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '14px',
    padding: '14px',
};

const statLabelStyle = { color: '#888', fontSize: '12px', marginBottom: '6px' };
const statValueStyle = { color: '#fff', fontSize: '16px', fontWeight: '700' };

const infoBoxStyle = {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '14px',
    padding: '4px 16px',
};

function WalletAssetDetailPage() {
    const { coinId }  = useParams();
    const navigate    = useNavigate();
    const location    = useLocation();
    const passedAsset = location.state || {};

    const { walletType, builtInWallet, sendFromBuiltIn } = useWallet();
    const ethAddr = walletType === 'builtin' && builtInWallet ? builtInWallet.ethereum.address : '';
    const solAddr = walletType === 'builtin' && builtInWallet ? builtInWallet.solana.address   : '';

    const symbol   = ( passedAsset.symbol || '' ).toUpperCase();
    const network  = passedAsset.network || 'ethereum';
    const balance  = parseFloat( passedAsset.balance || '0' );
    const isNative = NATIVE_SYMBOL_BY_NETWORK[ network ] === symbol;

    const [ coin, setCoin ]     = useState( null );
    const [ loading, setLoading ] = useState( true );
    const [ error, setError ]   = useState( null );
    const [ activity, setActivity ] = useState( [] );
    const [ activityLoading, setActivityLoading ] = useState( true );
    const [ showFullAbout, setShowFullAbout ] = useState( false );

    /* Fetch coin market data from CoinGecko */
    useEffect( () => {
        if ( ! coinId ) { setLoading( false ); setError( 'Unknown asset' ); return; }
        let cancelled = false;
        const load = async () => {
            setLoading( true );
            setError( null );
            try {
                const url = PROXY_URL + '/coins/' + coinId + '?localization=false&tickers=false&community_data=false&developer_data=false&sparkline=false';
                const res = await fetch( url, { headers: getCoinGeckoHeaders() } );
                if ( ! res.ok ) throw new Error( 'Failed to load coin data' );
                const json = await res.json();
                if ( ! cancelled ) setCoin( json );
            } catch ( e ) {
                if ( ! cancelled ) setError( 'Could not load price data. Please try again.' );
            } finally {
                if ( ! cancelled ) setLoading( false );
            }
        };
        load();
        return () => { cancelled = true; };
    }, [ coinId ] );

    /* Fetch activity — filtered to just this asset */
    useEffect( () => {
        let cancelled = false;
        const loadActivity = async () => {
            setActivityLoading( true );
            try {
                let txs = [];
                if ( network === 'solana' ) {
                    if ( symbol === 'SOL' ) {
                        const raw = solAddr ? await fetchSolTransactions( solAddr ) : [];
                        txs = raw.map( tx => ( { hash: tx.hash, isSent: tx.isSent, value: tx.value, timestamp: tx.timestamp } ) );
                    } else {
                        /* SPL token activity (USDT/USDC on Solana) isn't parsed yet —
                           fetchSolTransactions only tracks native SOL balance changes. */
                        txs = [];
                    }
                } else if ( isNative ) {
                    const raw = ethAddr ? await fetchTransactions( ethAddr, network ) : [];
                    txs = raw.map( tx => ( {
                        hash: tx.hash,
                        isSent: ( tx.from || '' ).toLowerCase() === ( ethAddr || '' ).toLowerCase(),
                        value: tx.value,
                        timestamp: tx.timestamp,
                    } ) );
                } else {
                    const raw = ethAddr ? await fetchTokenTransfers( ethAddr, network ) : [];
                    txs = raw
                        .filter( t => ( t.tokenSymbol || '' ).toUpperCase() === symbol )
                        .map( tx => ( {
                            hash: tx.hash,
                            isSent: ( tx.from || '' ).toLowerCase() === ( ethAddr || '' ).toLowerCase(),
                            value: tx.value,
                            timestamp: tx.timestamp,
                        } ) );
                }
                if ( ! cancelled ) setActivity( txs );
            } catch ( e ) {
                if ( ! cancelled ) setActivity( [] );
            } finally {
                if ( ! cancelled ) setActivityLoading( false );
            }
        };
        loadActivity();
        return () => { cancelled = true; };
    }, [ network, symbol, isNative, ethAddr, solAddr ] );

    const price         = coin?.market_data?.current_price?.usd ?? null;
    const change24hPct   = coin?.market_data?.price_change_percentage_24h ?? null;
    const value          = price != null ? balance * price : null;
    const change24hValue = ( value != null && change24hPct != null ) ? value * ( change24hPct / 100 ) : null;

    const [ showSendModal, setShowSendModal ]       = useState( false );
    const [ showReceiveModal, setShowReceiveModal ] = useState( false );
    const [ sendTo, setSendTo ]         = useState( '' );
    const [ sendAmount, setSendAmount ] = useState( '' );
    const [ isSending, setIsSending ]   = useState( false );

    const getDepositAddress = () => {
        if ( walletType === 'builtin' && builtInWallet ) {
            if ( network === 'solana' )  return builtInWallet.solana.address;
            if ( network === 'bitcoin' ) return builtInWallet.bitcoin.address;
            return builtInWallet.ethereum.address;
        }
        return '';
    };
    const depositAddress = getDepositAddress();

    const handleSend    = () => setShowSendModal( true );
    const handleReceive = () => setShowReceiveModal( true );
    const handleSwap     = () => navigate( '/exchange' );
    const handleBuy      = () => navigate( '/buy' );

    const handleSendSubmit = async () => {
        setIsSending( true );
        try {
            const result = await sendAsset( { asset: passedAsset, sendTo, sendAmount, walletType, builtInWallet, sendFromBuiltIn } );
            await resumeAudio();
            playSendSound();
            toast.success( result.hash ? `Sent! ${ result.hash.slice( 0, 16 ) }...` : 'Transaction submitted!' );
            setShowSendModal( false );
            setSendTo( '' );
            setSendAmount( '' );
        } catch ( err ) {
            console.error( err );
            playErrorSound();
            toast.error( err.message || 'Transaction failed.' );
        } finally {
            setIsSending( false );
        }
    };

    if ( loading ) {
        return (
            <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>
                Loading...
            </div>
        );
    }

    if ( error || ! coin ) {
        return (
            <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>
                <button onClick={ () => navigate( -1 ) } style={{ background: 'none', border: 'none', color: '#7C6FFF', cursor: 'pointer', marginBottom: '20px' }}>← Back</button>
                <div>{ error || 'Asset not found.' }</div>
            </div>
        );
    }

    const rawDescription = coin.description?.en ? coin.description.en.replace( /<[^>]*>/g, '' ) : '';
    const shortDescription = rawDescription.length > 200 && ! showFullAbout ? rawDescription.slice( 0, 200 ) + '...' : rawDescription;

    return (
        <div style={{ maxWidth: '720px', margin: '0 auto', padding: '20px 16px 60px' }}>

            <button onClick={ () => navigate( -1 ) } style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '20px', marginBottom: '16px' }}>←</button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                { ( passedAsset.icon || coin.image?.small ) && (
                    <img src={ passedAsset.icon || coin.image.small } alt={ coin.name } style={{ width: '32px', height: '32px', borderRadius: '50%' }} />
                ) }
                <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#fff', margin: 0 }}>{ coin.name }</h1>
            </div>

            { window.chainvaultSettings?.adSlot5 && (
                <div
                    className="cv-ad-slot"
                    style={{ margin: '0 0 20px', textAlign: 'center', minHeight: '60px' }}
                    dangerouslySetInnerHTML={{ __html: window.chainvaultSettings.adSlot5 }}
                />
            ) }

            <div style={{ marginBottom: '4px' }}>
                <span style={{ fontSize: '40px', fontWeight: '800', color: '#fff' }}>{ formatPrice( price ) }</span>
            </div>
            { change24hPct != null && (
                <div style={{ marginBottom: '24px', fontSize: '15px', color: change24hPct >= 0 ? '#4ADE80' : '#F87171' }}>
                    { change24hPct >= 0 ? '+' : '' }{ change24hPct.toFixed( 2 ) }% (24h)
                </div>
            ) }

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px', marginBottom: '28px' }}>
                <button onClick={ handleSend }    style={ actionBtnStyle }>↑ Send</button>
                <button onClick={ handleReceive } style={ actionBtnStyle }>↓ Receive</button>
                <button onClick={ handleSwap }    style={ actionBtnStyle }>⇄ Swap</button>
                <button onClick={ handleBuy  }    style={ actionBtnStyle }>$ Buy</button>
            </div>

            <div style={{ marginBottom: '28px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#fff', marginBottom: '12px' }}>Position</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div style={ statCardStyle }>
                        <div style={ statLabelStyle }>Value</div>
                        <div style={ statValueStyle }>{ value != null ? '$' + value.toFixed( 2 ) : '—' }</div>
                    </div>
                    <div style={ statCardStyle }>
                        <div style={ statLabelStyle }>Balance</div>
                        <div style={ statValueStyle }>{ balance.toFixed( 4 ) } { symbol }</div>
                    </div>
                    <div style={ statCardStyle }>
                        <div style={ statLabelStyle }>24h Change</div>
                        <div style={{ ...statValueStyle, color: ( change24hValue || 0 ) >= 0 ? '#4ADE80' : '#F87171' }}>
                            { change24hValue != null ? ( change24hValue >= 0 ? '+' : '-' ) + '$' + Math.abs( change24hValue ).toFixed( 2 ) : '—' }
                        </div>
                    </div>
                    <div style={ statCardStyle }>
                        <div style={ statLabelStyle }>Network</div>
                        <div style={ statValueStyle }>{ network.charAt( 0 ).toUpperCase() + network.slice( 1 ) }</div>
                    </div>
                </div>
            </div>

            <div style={{ marginBottom: '28px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#fff', marginBottom: '12px' }}>Info</h3>
                <div style={ infoBoxStyle }>
                    <InfoRow label="Market Cap" value={ coin.market_data?.market_cap?.usd ? '$' + coin.market_data.market_cap.usd.toLocaleString() : '—' } />
                    <InfoRow label="Total Supply" value={ coin.market_data?.total_supply ? coin.market_data.total_supply.toLocaleString() : '—' } />
                    <InfoRow label="Circulating Supply" value={ coin.market_data?.circulating_supply ? coin.market_data.circulating_supply.toLocaleString() : '—' } />
                    <InfoRow label="Created" value={ coin.genesis_date || '—' } last />
                </div>
            </div>

            { rawDescription && (
                <div style={{ marginBottom: '28px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#fff', marginBottom: '12px' }}>About</h3>
                    <p style={{ fontSize: '14px', color: '#aaa', lineHeight: '1.6' }}>{ shortDescription }</p>
                    { rawDescription.length > 200 && (
                        <button onClick={ () => setShowFullAbout( ! showFullAbout ) } style={{ background: 'none', border: 'none', color: '#7C6FFF', cursor: 'pointer', fontSize: '14px', padding: 0 }}>
                            { showFullAbout ? 'Show Less' : 'Show More' }
                        </button>
                    ) }
                    { coin.links?.homepage?.[0] && (
                        <div style={{ marginTop: '12px' }}>
                            <a href={ coin.links.homepage[0] } target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', background: 'rgba(255,255,255,0.06)', color: '#fff', padding: '8px 16px', borderRadius: '20px', fontSize: '13px', textDecoration: 'none' }}>
                                🌐 Website
                            </a>
                        </div>
                    ) }
                </div>
            ) }

            <div>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#fff', marginBottom: '12px' }}>Activity</h3>
                { activityLoading ? (
                    <div style={{ color: '#666', fontSize: '14px' }}>Loading activity...</div>
                ) : activity.length === 0 ? (
                    <div style={{ color: '#666', fontSize: '14px' }}>
                        { network === 'solana' && symbol !== 'SOL'
                            ? 'Activity for Solana tokens is coming soon.'
                            : 'No recent activity for this asset.' }
                    </div>
                ) : (
                    <div>
                        { activity.slice( 0, 10 ).map( ( tx, i ) => (
                            <div key={ tx.hash || i } style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: i < activity.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                                <div>
                                    <div style={{ color: '#fff', fontSize: '14px', fontWeight: '600' }}>
                                        { tx.isSent ? 'Sent' : 'Received' }
                                    </div>
                                    <div style={{ color: '#666', fontSize: '12px' }}>{ timeAgo( tx.timestamp ) }</div>
                                </div>
                                <div style={{ color: tx.isSent ? '#fff' : '#4ADE80', fontSize: '14px', fontWeight: '600' }}>
                                    { tx.isSent ? '-' : '+' }{ tx.value } { symbol }
                                </div>
                            </div>
                        ) ) }
                    </div>
                ) }
            </div>

            { window.chainvaultSettings?.adSlot6 && (
                <div
                    className="cv-ad-slot"
                    style={{ margin: '28px 0 0', textAlign: 'center', minHeight: '60px' }}
                    dangerouslySetInnerHTML={{ __html: window.chainvaultSettings.adSlot6 }}
                />
            ) }

            { showSendModal && (
                <div
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={ () => setShowSendModal( false ) }
                >
                    <div
                        style={{ background: '#0f0f22', borderRadius: '20px', padding: '24px', width: '100%', maxWidth: '400px', margin: '16px', border: '1px solid rgba(124,111,255,0.2)' }}
                        onClick={ e => e.stopPropagation() }
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <div style={{ fontSize: '16px', fontWeight: '700', color: '#fff' }}>Send { symbol }</div>
                            <button onClick={ () => setShowSendModal( false ) } style={{ background: 'none', border: 'none', color: '#888', fontSize: '18px', cursor: 'pointer' }}>✕</button>
                        </div>
                        <input
                            placeholder="Recipient address"
                            value={ sendTo }
                            onChange={ e => setSendTo( e.target.value ) }
                            style={{ width: '100%', background: '#1a1a35', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '12px', color: '#fff', fontSize: '14px', marginBottom: '12px', boxSizing: 'border-box' }}
                        />
                        <input
                            placeholder="Amount"
                            type="number"
                            value={ sendAmount }
                            onChange={ e => setSendAmount( e.target.value ) }
                            style={{ width: '100%', background: '#1a1a35', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '12px', color: '#fff', fontSize: '14px', marginBottom: '8px', boxSizing: 'border-box' }}
                        />
                        <div style={{ fontSize: '12px', color: '#888', marginBottom: '16px' }}>
                            Balance: { balance.toFixed( 6 ) } { symbol }
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button onClick={ () => setShowSendModal( false ) } style={{ flex: 1, padding: '12px', background: '#1a1a35', border: 'none', borderRadius: '10px', color: '#aaa', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                            <button onClick={ handleSendSubmit } disabled={ isSending } style={{ flex: 1, padding: '12px', background: 'linear-gradient(135deg,#7c6fff,#FC72FF)', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' }}>
                                { isSending ? 'Sending...' : 'Send' }
                            </button>
                        </div>
                    </div>
                </div>
            ) }

            { showReceiveModal && (
                <div
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={ () => setShowReceiveModal( false ) }
                >
                    <div
                        style={{ background: '#0f0f22', borderRadius: '20px', padding: '24px', width: '100%', maxWidth: '400px', margin: '16px', border: '1px solid rgba(124,111,255,0.2)' }}
                        onClick={ e => e.stopPropagation() }
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <div style={{ fontSize: '16px', fontWeight: '700', color: '#fff' }}>Receive { symbol }</div>
                            <button onClick={ () => setShowReceiveModal( false ) } style={{ background: 'none', border: 'none', color: '#888', fontSize: '18px', cursor: 'pointer' }}>✕</button>
                        </div>
                        <p style={{ fontSize: '13px', color: '#888', marginBottom: '16px' }}>
                            Send { symbol } to this address on the { network.charAt( 0 ).toUpperCase() + network.slice( 1 ) } network:
                        </p>
                        <div style={{ background: '#1a1a35', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '12px', color: '#fff', fontSize: '13px', wordBreak: 'break-all', marginBottom: '16px' }}>
                            { depositAddress || 'No address available' }
                        </div>
                        <button
                            onClick={ () => { navigator.clipboard.writeText( depositAddress ); toast.success( 'Address copied!' ); } }
                            style={{ width: '100%', padding: '12px', background: 'linear-gradient(135deg,#7c6fff,#FC72FF)', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' }}
                        >
                            Copy Address
                        </button>
                    </div>
                </div>
            ) }

        </div>
    );
}

export default WalletAssetDetailPage;
